import algoliasearch from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';

import '@algolia/autocomplete-theme-classic';

import { getValueFromMeta } from '../utils/getValueFromMeta';
import { debounced } from './autocomplete/utils/debouncer';

import { ALGOLIA_APP_ID, ALGOLIA_APP_KEY } from './algolia-shared/config';

const env = (getValueFromMeta('x-env') ? getValueFromMeta('x-env') : 'live');
const indexName = `${env}_Pages`;

const searchClient = algoliasearch(
    ALGOLIA_APP_ID,
    ALGOLIA_APP_KEY,
);

const mySources = [];

/*
    1. Use our predefined facets (all options for 'objectType')
 */
const filtersMap = {
    Page: 'Inspiration',
    Event: 'Events',
    Listing: 'Activities, Food & Drink, Accommodation',
};

/*
    2. Init autocomplete with multiple sources (1 source for
    each category option)
*/
function autocompleteInit() {

    // eslint-disable-next-line
    for (const key in filtersMap) {

        const facet = filtersMap[key] ? filtersMap[key] : key;

        mySources.push(
            {
                sourceId: key,
                getItems({ query }) {

                    return getAlgoliaResults({
                        searchClient,
                        queries: [
                            {
                                indexName,
                                query,
                                params: {
                                    hitsPerPage: 3,
                                    facetFilters: [
                                        'objectType:' + key,
                                    ],
                                    filters: 'objectSubsite:0',
                                },
                            },
                        ],
                    });

                },
                templates: {
                    header({ html }) {

                        return html`
                            <div>
                                <span class="aa-SourceHeaderTitle">${facet}</span>
                                <div class="aa-SourceHeaderLine" />
                            </div>
                        `;

                    },
                    item({ item, html }) {

                        return html`
                            <a href="${item.objectLink}" class="aa-ItemContent justify-start" title="Read more about ${item.objectTitle}">
                                <div class="aa-ItemIcon aa-ItemIcon--alignTop">
                                    <img
                                        src="${item.objectImage}"
                                        alt="${item.objectTitle}"
                                        width="40"
                                        height="40"
                                    />
                                </div>
                                <div class="aa-ItemContentBody">
                                    <p class="h6 text-primary mb-0">
                                        ${item.objectTitle}
                                    </p>
                                    <p class="text-sm whitespace-nowrap overflow-hidden text-ellipsis mb-0">
                                        ${item.objectSummary}
                                    </p>
                                </div>
                            </a>
                        `;

                    },
                    footer({ html, state }) {

                        return html`
                            <div class="text-center">
                                <a href="/search?query=${encodeURIComponent(state.query)}" class="button mb-0">See All Results</a>
                            </div>
                        `;

                    },
                },
                getItemUrl({ item }) {

                    return item.objectLink;

                },
            },
        );

    }

    // Initialize the autocomplete when the placeholder is set
    document.addEventListener('placeholderSet', (event) => {

        const placeholder = event.detail;

        // Prevent duplicate initialization
        if (document.querySelector('#algolia-autocomplete input')) {

            return;

        }

        // eslint-disable-next-line
        const autoComplete = autocomplete({
            container: '#algolia-autocomplete',
            // eslint-disable-next-line
            placeholder: placeholder,
            detachedMediaQuery: 'none',
            getSources() {

                return debounced(mySources);

            },
            onSubmit({ state }) {

                // Check if the query has at least one character
                if (state.query.trim().length === 0) {

                    // eslint-disable-next-line
                    alert('Please enter at least one character.');

                } else {

                    window.location.href = `/search?query=${encodeURIComponent(state.query)}`;

                }

            },
        });

    });

}

const autocompleteInitAccessible = () => {

    const autocompleteInput = document.getElementById('autocomplete-0-input');

    // eslint-disable-next-line
    const handleAutocompleteResultsCreation = (mutationsList, observer) => {

        mutationsList.forEach((mutation) => {

            mutation.addedNodes.forEach((node) => {

                if (node.nodeType === 1 && node.classList.contains('aa-Panel')) {

                    // results dropdown elem is created
                    document.querySelector('.aa-Panel').setAttribute('role', 'status');
                    observer.disconnect();

                }

            });

        });

    };

    // Input accessibility properties
    if (autocompleteInput) {

        autocompleteInput.setAttribute('aria-label', 'Site Search');
        autocompleteInput.setAttribute('role', 'search');

        // Create a MutationObserver instance for creation of autocomplete results dropdown
        const observer = new MutationObserver(handleAutocompleteResultsCreation);
        const observerOptions = { childList: true, subtree: true };
        observer.observe(document.body, observerOptions);

    }

};

autocompleteInit();
autocompleteInitAccessible();
